/**
 * Typed accessors for required environment variables.
 *
 * These throw rather than falling back, so a misconfigured server fails on
 * startup (see `src/instrumentation.ts`) instead of silently serving wrong
 * canonical URLs, social cards, sitemap entries and email links.
 */

import { BASE_PATH } from "./basePath";

/**
 * Absolute origin the app is served from, without a trailing slash.
 *
 * @throws if `NEXT_PUBLIC_BASE_URL` is missing or not an http(s) origin.
 */
export function getBaseUrl(): string {
  const value = process.env.NEXT_PUBLIC_BASE_URL;

  if (!value) {
    throw new Error(
      "NEXT_PUBLIC_BASE_URL is not set. It is required for canonical URLs, social cards, sitemaps and email links.",
    );
  }

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error(`NEXT_PUBLIC_BASE_URL is not a valid URL: "${value}"`);
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new Error(`NEXT_PUBLIC_BASE_URL must use http or https: "${value}"`);
  }

  if (
    parsed.pathname !== "/" ||
    parsed.search ||
    parsed.hash ||
    parsed.username ||
    parsed.password
  ) {
    throw new Error(
      "NEXT_PUBLIC_BASE_URL must be an origin without a path, query, hash, or credentials",
    );
  }

  return parsed.origin;
}

/** Absolute URL of the app root: the origin plus the basePath. */
export function getAppUrl(): string {
  return `${getBaseUrl()}${BASE_PATH}`;
}

/**
 * Parses `NEXT_PUBLIC_DISCOVER_MARKETS` (for example `GB,NO,DE`) into the
 * market codes it names. `undefined` means "use the registry defaults".
 *
 * @throws on a code that is not in `known`, so a typo fails the build.
 */
export function parseMarketList<Code extends string>(
  value: string | undefined,
  known: readonly Code[],
): Code[] | undefined {
  if (!value) {
    return undefined;
  }

  const normalized = value
    .split(",")
    .map((code) => code.trim().toUpperCase() as Code);
  if (normalized.some((code) => !known.includes(code))) {
    throw new Error(
      `Market code "${normalized.join(", ")}" is not supported yet.`,
    );
  }
  return Array.from(new Set(normalized));
}
